import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'GoFundMe Alternatives for Missionaries: 8 Platforms Compared (2026)';
const DESCRIPTION =
    "An honest, founder-written comparison of eight platforms missionaries actually use in 2026 — the real fees, the trade-offs, and a decision tree for which one fits you.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover2.jpg',
            datePublished: '2026-07-20',
            dateModified: '2026-09-30',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/gofundme-alternatives-for-missionaries',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: "What's the best GoFundMe alternative for missionaries?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'It depends on your donor base. If most donors itemize U.S. taxes, a 501(c)(3) fiscal sponsor like Modern Day Missions wins on the math. If you want the lowest fee at volume, Continue to Give. If you want recurring-first design and built-in supporter tooling, Devotion Fund.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is GoFundMe good for ongoing monthly missionary support?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Not really. GoFundMe charges 5% on recurring donations — the same as Devotion Fund's bank-transfer rate (Devotion Fund's card rate is 7%) — but without any of the donor CRM, update tooling, or recurring-first design built for a multi-year support relationship.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Which platform is cheapest for missionaries?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "On raw fees, Continue to Give's sending-org rate (2.7% + $0.25) is typically the cheapest, followed by GiveSendGo, HopeGives (processing-only, no platform fee), and GoFundMe. Modern Day Missions can be cheapest in practice for donors who itemize, once tax deductibility is factored in.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is there a 0%-fee alternative to GiveSendGo?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes — HopeGives also charges 0% platform fee (just standard PayPal processing), and its donor tip is genuinely opt-in and not pre-checked at donation, unlike GiveSendGo's prominent 10% suggested tip.",
                    },
                },
            ],
        },
    ],
};

export default function GofundmeAlternativesForMissionaries() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/gofundme-alternatives-for-missionaries' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-gfm-alternatives-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover2.jpg" alt="A country road forking toward an open field under a stormy sky" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>A missionary in Thailand emailed me last month: <em>"GoFundMe just took $217 out of a $4,000 month. Is there anything better?"</em></p>

                    <p>There is — but the honest answer is more interesting than the marketing pages. GoFundMe isn't built for monthly missionary support; it's built for one-time emergency fundraisers. The question isn't "what's cheaper than GoFundMe" — it's "what's actually designed for someone raising recurring support over years, not weeks."</p>

                    <p>This is a comparison of eight platforms missionaries actually use in 2026, with the math, the trade-offs each one makes, and where each one is the right call. I run Devotion Fund — one of the eight — so I've called out where we lose, plainly.</p>

                    <h2>The 8 platforms at a glance</h2>

                    <table>
                        <thead>
                            <tr>
                                <th>Platform</th>
                                <th>Platform fee</th>
                                <th>Processing fee</th>
                                <th>Recurring-first?</th>
                                <th>Tax-deductible?</th>
                                <th>Best for</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>GoFundMe</strong></td>
                                <td>0%</td>
                                <td>2.9% + $0.30 (5% on recurring)</td>
                                <td>No</td>
                                <td>No</td>
                                <td>One-time emergency / project</td>
                            </tr>
                            <tr>
                                <td><strong>GiveSendGo</strong></td>
                                <td>0%</td>
                                <td>2.7% + $0.30 (+ optional 10% donor tip)</td>
                                <td>Limited</td>
                                <td>No</td>
                                <td>Christians wanting a 0% headline</td>
                            </tr>
                            <tr>
                                <td><strong>Continue to Give</strong></td>
                                <td>$0 on the giving tier (ChMS + accounting are paid add-ons)</td>
                                <td>2.7% + $0.25 via a sending org; 3.9% + $0.45 independent</td>
                                <td>Yes</td>
                                <td>Yes, via a sending org</td>
                                <td>Missionaries wanting low fees + full ChMS/accounting</td>
                            </tr>
                            <tr>
                                <td><strong>Pure Charity</strong></td>
                                <td>Custom monthly</td>
                                <td>+ processing</td>
                                <td>Yes (org-side)</td>
                                <td>Yes (501(c)(3))</td>
                                <td>Organizations and teams, not solo individuals</td>
                            </tr>
                            <tr>
                                <td><strong>Modern Day Missions</strong></td>
                                <td>~4.28% effective (6% headline)</td>
                                <td>Included</td>
                                <td>Yes</td>
                                <td>Yes — 501(c)(3) fiscal sponsor</td>
                                <td>U.S. missionaries with itemizing donors</td>
                            </tr>
                            <tr>
                                <td><strong>Patreon</strong></td>
                                <td>10%</td>
                                <td>+ ~2.9% processing + currency conversion (~13–16% all-in)</td>
                                <td>Yes</td>
                                <td>No</td>
                                <td>Creators with an existing Patreon audience</td>
                            </tr>
                            <tr>
                                <td><strong>HopeGives</strong></td>
                                <td>0%</td>
                                <td>2.9% + $0.30 (standard PayPal rate) + optional, opt-in "Impact Tip"</td>
                                <td>Yes</td>
                                <td>No</td>
                                <td>Churches, denominations, and nonprofits as well as missionaries</td>
                            </tr>
                            <tr>
                                <td><strong>Devotion Fund</strong></td>
                                <td>5-7% flat (all-in)</td>
                                <td>Included (5% bank transfer, 7% card)</td>
                                <td>Yes (recurring-first by design)</td>
                                <td>No (yet)</td>
                                <td>Missionaries who want recurring support, direct payouts, and modern updates</td>
                            </tr>
                        </tbody>
                    </table>

                    <p><small>All numbers verified against each platform's public pricing page. Devotion Fund's fee is all-in — 5% on bank transfers, 7% on card — card processing is absorbed, not added on top.</small></p>

                    <h2>1. GoFundMe</h2>

                    <p><strong>Headline:</strong> 0% platform fee. <strong>Reality:</strong> 2.9% + $0.30 per donation on one-time gifts, <strong>5% on recurring</strong>, plus an optional donor tip that defaults to a non-trivial percentage.</p>

                    <p>GoFundMe is the default search result, which is exactly why missionaries land there — and exactly why it's almost never the right tool. The platform is optimized for emergencies and short campaigns. There is no concept of a "support partner" relationship, no recurring-by-default flow, no donor management. Donors give once and forget.</p>

                    <p><strong>Where it wins:</strong> Brand recognition. A non-Christian relative will give on GoFundMe without hesitation. For a single short-term-trip ask, that lower friction can be worth the fee.</p>

                    <p><strong>Where it loses:</strong> Recurring monthly support is what funds a missionary career. GoFundMe's recurring rate is 5% — the same as Devotion Fund's bank-transfer rate (Devotion Fund's card rate is 7%) but without the supporter relationship tools.</p>

                    <h2>2. GiveSendGo</h2>

                    <p><strong>Headline:</strong> 0% platform fee. <strong>Reality:</strong> 2.7% + $0.30 processing, plus a <em>requested</em> 10% donor tip that's prominent at checkout.</p>

                    <p>GiveSendGo is the Christian-flavored GoFundMe. The 0% headline is real on the platform side. The 10% donor-suggested tip is the actual revenue model, and it's framed as Christian generosity rather than as a fee.</p>

                    <p><strong>Where it wins:</strong> Donors who already use GiveSendGo know the tip exists and choose to give it. The brand reads as more aligned with Christian missions than GoFundMe.</p>

                    <p><strong>Where it loses:</strong> Recurring support tooling is thin. The political associations the brand has picked up over the last few years are worth a conversation with your sending church before you choose it — not every sending agency wants to be associated. (We go deeper on this specific comparison in <a href="/resources/givesendgo-vs-devotion-fund">GiveSendGo vs Devotion Fund</a>.)</p>

                    <h2>3. Continue to Give</h2>

                    <p><strong>Headline:</strong> Fees "as low as 0.5%." <strong>Reality:</strong> Tiered — a low percentage <em>plus</em> a $10/month subscription on the lowest tier, scaling up from there.</p>

                    <p>Continue to Give is the most under-marketed platform in the category. It's been built for ministry fundraising specifically and supports recurring giving, text-to-give, and (through partners) some tax-deductible flows.</p>

                    <p><strong>Where it wins:</strong> If you're processing $1,000+/month and you don't mind the monthly subscription, your effective fee rate is genuinely the lowest in this list.</p>

                    <p><strong>Where it loses:</strong> The UX is dated; the missionary-facing supporter experience is functional but not warm. Setup takes longer than competitors. The 0.5% headline only applies at higher tiers — at $500/month support you're closer to 2.5–3% effective. (Full breakdown: <a href="/resources/continue-to-give-vs-devotion-fund">Continue to Give vs Devotion Fund</a>.)</p>

                    <h2>4. Pure Charity</h2>

                    <p><strong>Headline:</strong> Custom monthly pricing for organizations.</p>

                    <p>Pure Charity is not built for solo missionaries. It's built for sending organizations, mission trips, and team-based campaigns running under an umbrella nonprofit. Pricing isn't published; it's negotiated.</p>

                    <p><strong>Where it wins:</strong> If you're a missions pastor or agency leader running a centralized campaign for multiple teams, Pure Charity's compliance + 501(c)(3) infrastructure is solid.</p>

                    <p><strong>Where it loses:</strong> Individual missionaries can't realistically self-onboard. If you're reading this article wondering what platform to pick for yourself, this isn't it.</p>

                    <h2>5. Modern Day Missions</h2>

                    <p><strong>Headline:</strong> 6% admin fee. <strong>Reality:</strong> Closer to ~4.28% effective after their large-gift policy and the "donor covers fees" feature — and donations are <strong>tax-deductible because Modern Day Missions is a 501(c)(3) fiscal sponsor.</strong></p>

                    <p>This is the most important alternative in this list, and the one I'd recommend ahead of Devotion Fund for one specific donor profile.</p>

                    <p><strong>Where it wins:</strong> If a meaningful share of your supporters itemize their U.S. taxes — and especially if you have anyone giving $1,000+ a year — the deductibility almost certainly outweighs the fee differential. A $5,000 annual giver in the 24% bracket recovers $1,200 in tax savings. That swamps a 1.7-point fee gap.</p>

                    <p><strong>Where it loses:</strong> Not available outside the U.S. fiscal-sponsorship framework. Tighter restrictions on how funds are used (because of 501(c)(3) governance). No native recurring-creator UX — it's a fiscal sponsor, not a creator platform.</p>

                    <h2>6. Patreon</h2>

                    <p><strong>Headline:</strong> 10% for new creators (5-8% legacy tiers for pre-August-2025 accounts). <strong>Reality:</strong> ~10% platform + ~2.9% processing + currency conversion fees = <strong>13–16% all-in</strong> for most creators with international support, which is most missionaries.</p>

                    <p>Patreon is the platform most missionaries land on by accident because it's the platform they already know from podcasts and YouTubers.</p>

                    <p><strong>Where it wins:</strong> If you've already built a Patreon audience and your supporters are habituated to the brand, the switching cost may not be worth it. Patreon's creator tools (tiered rewards, member-only posts) are mature.</p>

                    <p><strong>Where it loses:</strong> The all-in fees are the highest in this list by a wide margin. The category mismatch is real — Patreon is built for entertainment creators, not for partnership-style support raising. Many supporters churn because Patreon feels transactional.</p>

                    <h2>7. HopeGives</h2>

                    <p><strong>Headline:</strong> 0% platform fee. <strong>Reality:</strong> 2.9% + $0.30 standard PayPal processing, plus an optional "Impact Tip" at donor checkout.</p>

                    <p>HopeGives is newer than GiveSendGo and smaller than Sowfund, but it does two things worth taking seriously: its donor tip is genuinely opt-in and not pre-checked (a more honest ask than GiveSendGo's prominent 10% default), and it ships its own AI writing tool for campaign messages — the same category of feature as our AI-assisted prayer-letter draft.</p>

                    <p><strong>Where it wins:</strong> Broader than a single-missionary tool — built for churches, denominations, and nonprofits too. On raw fees, cheaper than our bank-transfer rate above roughly $14/month, and cheaper than our card rate above roughly $7/month.</p>

                    <p><strong>Where it loses:</strong> Being built for churches and nonprofits as well as individuals means it isn't optimized around one missionary's ongoing support relationship the way a single-purpose platform is. (Full breakdown: <a href="/resources/hopegives-vs-devotion-fund">HopeGives vs Devotion Fund</a>.)</p>

                    <h2>8. Devotion Fund</h2>

                    <p><strong>Headline:</strong> 5-7% flat. <strong>Reality:</strong> 5% all-in on bank transfer, 7% all-in on card — processing is absorbed inside that number, so a $100 monthly gift costs $105 (bank) or $107 (card), not that plus a separate processing fee on top. No donor tip, no monthly subscription, no tiered pricing games.</p>

                    <p>I built Devotion Fund because every existing platform forced missionaries to choose between two of these three things, and I wanted them to have all three: low all-in fee, recurring-first design, and a real supporter relationship (updates, donor management, AI-assisted prayer-letter drafting).</p>

                    <p><strong>Where it wins:</strong> Recurring-by-default flow. Direct payouts to your bank, not held by a fiscal sponsor. AI prayer-letter draft tool built in. Donor CRM with retention metrics.</p>

                    <h3>Where Devotion Fund is weaker</h3>

                    <p>Three honest losses worth naming up front:</p>

                    <ol>
                        <li><strong>No fiscal-sponsor tax deductibility yet.</strong> If most of your donors itemize, Modern Day Missions is the better recommendation for now. We're working on a fiscal-sponsor partnership; until then, this is a real gap.</li>
                        <li><strong>Not the cheapest on raw fees — only Patreon is reliably more expensive.</strong> At typical gift sizes, GoFundMe, GiveSendGo, both of Continue to Give's rates, Modern Day Missions, and HopeGives (above roughly $14/month bank transfer, $7/month card) all beat our 5-7% all-in. Where we win is the bundle — recurring tooling, supporter accounts, updates — not the headline rate.</li>
                        <li><strong>We're new.</strong> Soft-launched in early 2026. Less than a year of track record. If platform stability over five years is what you need to show your supporters, we don't have it yet — though our infrastructure is built on the same Stripe rails as every platform here.</li>
                    </ol>

                    <h2>What I'd actually recommend (decision tree)</h2>

                    <p><strong>If most of your donors itemize their U.S. taxes and you process $25K+/year:</strong> Modern Day Missions. The tax-deductibility math is decisive. Honestly.</p>

                    <p><strong>If you need the absolute lowest fees and you're tech-comfortable, processing $1,500+/month:</strong> Continue to Give. The subscription model gets cheap at volume.</p>

                    <p><strong>If you're already on Patreon with an active audience and the switching cost feels prohibitive:</strong> Stay on Patreon for now, but run the numbers — at most support levels you're leaving four-figure annual amounts on the table.</p>

                    <p><strong>If you're running a one-time short-term-trip fundraiser to a non-Christian network:</strong> GoFundMe. The brand-recognition friction reduction is real.</p>

                    <p><strong>If you want a recurring-first platform built for missionaries, with the supporter-relationship tooling and a clean 5-7% all-in fee:</strong> Devotion Fund.</p>

                    <p><strong>If you want a 0%-fee platform with an opt-in (not pre-checked) donor tip, and your need is broader than a single missionary's page:</strong> HopeGives.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>What's the best GoFundMe alternative for missionaries?</strong><br />
                    It depends on your donor base. If most donors itemize U.S. taxes, a 501(c)(3) fiscal sponsor like Modern Day Missions wins on the math. If you want the lowest fee at volume, Continue to Give. If you want recurring-first design and built-in supporter tooling, Devotion Fund.</p>

                    <p><strong>Is GoFundMe good for ongoing monthly missionary support?</strong><br />
                    Not really. GoFundMe charges 5% on recurring donations — the same as Devotion Fund's bank-transfer rate (Devotion Fund's card rate is 7%) — but without any of the donor CRM, update tooling, or recurring-first design built for a multi-year support relationship.</p>

                    <p><strong>Which platform is cheapest for missionaries?</strong><br />
                    On raw fees, Continue to Give's sending-org rate (2.7% + $0.25) is typically the cheapest, followed by GiveSendGo, HopeGives (processing-only, no platform fee), and GoFundMe. Modern Day Missions can be cheapest in practice for donors who itemize, once tax deductibility is factored in.</p>

                    <p><strong>Is there a 0%-fee alternative to GiveSendGo?</strong><br />
                    Yes — HopeGives also charges 0% platform fee (just standard PayPal processing), and its donor tip is genuinely opt-in and not pre-checked at donation, unlike GiveSendGo's prominent 10% suggested tip.</p>

                    <p><strong>Can I donate without creating an account?</strong><br />
                    Yes, on all three: GoFundMe, GiveSendGo and Devotion Fund all support guest checkout — a donor can give a one-time or recurring gift with just an email address, no sign-up required.</p>

                    <h2>The calculator</h2>

                    <p>Run your own monthly support level against all seven platforms with the free fee calculator — no signup, no email capture, just numbers: <a href="/calculator">devotionfund.com/calculator</a>.</p>

                    <h2>Next reads</h2>
                    <ul>
                        <li><a href="/resources/true-cost-missionary-fundraising-platforms-2026">The True Cost of Every Missionary Fundraising Platform in 2026</a> — the full cornerstone breakdown.</li>
                        <li><a href="/resources/continue-to-give-vs-devotion-fund">Continue to Give vs Devotion Fund: A Brutally Honest Comparison</a></li>
                        <li><a href="/resources/hopegives-vs-devotion-fund">HopeGives vs Devotion Fund: 0% Fee, Opt-In Tip, and an AI Writer of Its Own</a></li>
                        <li><a href="/resources/how-to-start-monthly-giving-page-without-501c3">How to Start a Monthly Giving Page as a Missionary (Without Becoming a 501(c)(3))</a></li>
                        <li><a href="/resources/venmo-and-cashapp-for-missionary-support-raising">Venmo and Cash App for Missionary Support Raising: Why They're Riskier Than They Look</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built this platform after watching missionary friends lose a double-digit share of their support to fees they didn't understand they were paying. Devotion Fund is one of eight platforms in this comparison and we have skin in this game — which is why every number above cites a public pricing page, and why we've named the cases where our own platform isn't the right call.</p>
                    <p><small><em>Last updated: 2026-09-30.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/gofundme-alternatives-for-missionaries" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
